import { GRID_SIZE, SCALE_MODES, TICK_BORDER_WIDTH, NL_TILE_SNAP_THRESHOLD, } from '../constants/numberLineConfig';
// --- Fraction math utilities ---
export var gcd = function (a, b) {
    if (!Number.isFinite(a) || !Number.isFinite(b))
        return 1;
    if (a === 0 && b === 0)
        return 1;
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
};
export var decimalToFraction = function (decimal) {
    if (decimal === 0)
        return { numerator: 0, denominator: 1 };
    var sign = decimal < 0 ? -1 : 1;
    decimal = Math.abs(decimal);
    var tolerance = 1.0e-6;
    var h1 = 1, h2 = 0, k1 = 0, k2 = 1;
    var b = decimal;
    do {
        var a = Math.floor(b);
        var aux = h1;
        h1 = a * h1 + h2;
        h2 = aux;
        aux = k1;
        k1 = a * k1 + k2;
        k2 = aux;
        b = 1 / (b - a);
    } while (Math.abs(decimal - h1 / k1) > decimal * tolerance && k1 < 10000);
    return { numerator: h1 * sign, denominator: k1 };
};
export var fractionToMixed = function (numerator, denominator) {
    if (denominator === 0)
        return { integer: 0, numerator: 0, denominator: 1 };
    var integer = Math.floor(Math.abs(numerator) / denominator) * (numerator < 0 ? -1 : 1);
    var remainder = Math.abs(numerator % denominator);
    return { integer: integer, numerator: remainder, denominator: denominator };
};
export var mixedToFraction = function (integer, numerator, denominator) {
    var improperNumerator = integer * denominator + numerator;
    return { numerator: improperNumerator, denominator: denominator };
};
export var fractionToDecimal = function (numerator, denominator) {
    if (denominator === 0)
        return 0;
    return numerator / denominator;
};
/**
 * Format a label value for CSS-based rendering based on numberType.
 * Returns strings parseable by NumberLineLabel:
 * - integer: "5", "-3"
 * - fraction: "\\frac{1}{2}", "-\\frac{3}{4}"
 * - mixed: "1\\frac{1}{2}", "-2\\frac{3}{4}"
 * - decimal: "0.5", "1.25"
 */
export var formatLabelForNumberType = function (value, numberType, referenceDenominator) {
    if (numberType === 0) {
        // Integer
        if (Number.isInteger(value))
            return value.toString();
        return parseFloat(value.toFixed(6)).toString();
    }
    if (numberType === 3) {
        // Decimal
        var str = parseFloat(value.toFixed(2)).toString();
        return str;
    }
    // Fraction or mixed number
    var den = referenceDenominator > 0 ? referenceDenominator : 1;
    // Both fraction and mixed number: 0 always renders as 0/den (e.g. 0/1, 0/8).
    if (value === 0) {
        return "\\frac{0}{".concat(den, "}");
    }
    var isNegative = value < 0;
    var absValue = Math.abs(value);
    var sign = isNegative ? '-' : '';
    // Use the reference denominator to compute the numerator. Do NOT simplify —
    // fraction labels must preserve the line's denominator (e.g. all eighths on
    // a 1/8-interval line, so 2/8 stays 2/8 rather than becoming 1/4).
    var totalNumerator = Math.round(absValue * den);
    if (totalNumerator === 0)
        return numberType === 1 ? "\\frac{0}{".concat(den, "}") : '0';
    if (numberType === 1) {
        // Pure fraction — always keep the reference denominator, never simplify
        // to a whole number (e.g. 2/2 stays 2/2, not 1).
        return "".concat(sign, "\\frac{").concat(totalNumerator, "}{").concat(den, "}");
    }
    // Mixed number (numberType === 2)
    var integerPart = Math.floor(totalNumerator / den);
    var remainderNum = totalNumerator % den;
    // Whole multiples render as a plain integer — a fraction never shows as
    // x/x (e.g. 2/2 → 1, 1 2/2 → 2), matching how a mixed number is actually
    // read aloud.
    if (remainderNum === 0) {
        return "".concat(sign).concat(integerPart);
    }
    if (integerPart === 0) {
        return "".concat(sign, "\\frac{").concat(remainderNum, "}{").concat(den, "}");
    }
    return "".concat(sign).concat(integerPart, "\\frac{").concat(remainderNum, "}{").concat(den, "}");
};
/**
 * Parse a string value to a number.
 * Supports whole numbers ("10"), decimals ("2.5"), fractions ("1/2"),
 * mixed numbers ("3 1/4"), and negative values ("-5", "-1/2").
 */
export var parseValueToNumber = function (value) {
    if (!value || value.trim() === '')
        return 0;
    var trimmed = value.trim();
    // Check for negative
    var isNegative = trimmed.startsWith('-');
    var abs = isNegative ? trimmed.slice(1).trim() : trimmed;
    var result;
    // Mixed number: "3 1/4"
    if (abs.includes(' ') && abs.includes('/')) {
        var parts = abs.split(' ');
        var whole = parseFloat(parts[0]);
        var fractionParts = parts[1].split('/');
        var num = parseFloat(fractionParts[0]);
        var den = parseFloat(fractionParts[1]);
        result = whole + (den !== 0 ? num / den : 0);
    }
    // Simple fraction: "1/2"
    else if (abs.includes('/')) {
        var fractionParts = abs.split('/');
        var num = parseFloat(fractionParts[0]);
        var den = parseFloat(fractionParts[1]);
        result = den !== 0 ? num / den : 0;
    }
    // Whole number or decimal
    else {
        result = parseFloat(abs);
    }
    if (isNaN(result))
        return 0;
    return isNegative ? -result : result;
};
/**
 * Format a number to display matching the input format.
 * If the interval was entered as a fraction, display as fraction.
 * If decimal, display as decimal. Otherwise whole number.
 */
export var formatLabel = function (value, inputFormat) {
    // Determine display format from the input
    var isFractionInput = inputFormat.includes('/');
    var isDecimalInput = inputFormat.includes('.') && !inputFormat.includes('/');
    if (isFractionInput) {
        return formatAsFraction(value, inputFormat);
    }
    if (isDecimalInput) {
        var decimalPlaces = (inputFormat.split('.')[1] || '').length;
        return value.toFixed(decimalPlaces);
    }
    // Whole number - show as integer if possible
    if (Number.isInteger(value)) {
        return value.toString();
    }
    // Fallback: show with reasonable precision
    return parseFloat(value.toFixed(6)).toString();
};
/**
 * Format a number as a fraction string.
 * Tries to match the denominator from the input format.
 */
var formatAsFraction = function (value, inputFormat) {
    if (value === 0)
        return '0';
    // Extract denominator from input format
    var denominator = 1;
    var formatTrimmed = inputFormat.trim().replace(/^-/, '');
    if (formatTrimmed.includes(' ') && formatTrimmed.includes('/')) {
        // Mixed number format
        var parts = formatTrimmed.split(' ');
        var fracParts = parts[1].split('/');
        denominator = parseInt(fracParts[1]) || 1;
    }
    else if (formatTrimmed.includes('/')) {
        var fracParts = formatTrimmed.split('/');
        denominator = parseInt(fracParts[1]) || 1;
    }
    var isNegative = value < 0;
    var absValue = Math.abs(value);
    var whole = Math.floor(absValue);
    var remainder = absValue - whole;
    var sign = isNegative ? '-' : '';
    if (remainder === 0) {
        return "".concat(sign).concat(whole);
    }
    var numerator = Math.round(remainder * denominator);
    if (whole === 0) {
        return "".concat(sign).concat(numerator, "/").concat(denominator);
    }
    return "".concat(sign).concat(whole, " ").concat(numerator, "/").concat(denominator);
};
/**
 * Compute tick mark positions along the number line.
 */
export var computeTickPositions = function (startNum, endNum, intervalNum, pxPerUnit) {
    if (intervalNum <= 0 || startNum >= endNum)
        return [];
    var ticks = [];
    var epsilon = intervalNum * 0.001;
    for (var val = startNum; val <= endNum + epsilon; val += intervalNum) {
        var roundedVal = startNum + Math.round((val - startNum) / intervalNum) * intervalNum;
        if (roundedVal > endNum + epsilon)
            break;
        ticks.push({
            value: roundedVal,
            px: (roundedVal - startNum) * pxPerUnit,
        });
    }
    return ticks;
};
/**
 * Compute which tick marks get a number label.
 *
 * "Label every N tick marks" is a whole-number COUNT, not a unit spacing:
 * label the first tick (index 0), skip N-1, label the next, and so on until
 * the ticks run out. The last tick may or may not be labeled. The value and
 * format of each labeled tick come straight from the tick itself, so labels
 * always sit exactly on a tick mark.
 */
export var computeLabelPositions = function (ticks, labelEveryN, inputFormat, numberType, referenceDenominator) {
    if (labelEveryN <= 0 || ticks.length === 0)
        return [];
    var labels = [];
    var useNumberType = numberType !== undefined && numberType > 0;
    for (var i = 0; i < ticks.length; i += labelEveryN) {
        var tick = ticks[i];
        labels.push({
            value: tick.value,
            px: tick.px,
            formatted: useNumberType
                ? formatLabelForNumberType(tick.value, numberType, referenceDenominator !== null && referenceDenominator !== void 0 ? referenceDenominator : 1)
                : formatLabel(tick.value, inputFormat),
        });
    }
    return labels;
};
/**
 * Compute total line width in pixels from range and scale.
 */
/**
 * Compute pixels per logical unit on a number line.
 *
 * - `scalingToManipulatives === true` (Fraction Tiles / Colored Tiles): the
 *   manipulative dictates a fixed grid-squares-per-unit ratio (12 for fraction
 *   tiles, 2 for colored tiles), so pxPerUnit must NOT depend on intervalNum.
 *   Interval only controls where ticks appear within that fixed ratio — e.g.
 *   in Fraction Tiles mode 1 unit is always 12 grid squares whether the user
 *   sets interval = 1, 0.5, or 2.
 * - `scalingToManipulatives === false` (no-scaling / "other"): pxPerUnit
 *   stretches with smaller intervals so each interval step occupies
 *   `gridSquaresPerUnit` grid squares visually. (Existing flexible behavior.)
 */
export var computePxPerUnit = function (gridSquaresPerUnit, intervalNum, scalingToManipulatives) {
    var baseUnit = gridSquaresPerUnit * GRID_SIZE;
    if (scalingToManipulatives)
        return baseUnit;
    return intervalNum > 0 ? baseUnit / intervalNum : baseUnit;
};
export var computeLineWidth = function (startNum, endNum, gridSquaresPerUnit, intervalNum, scalingToManipulatives) {
    if (intervalNum === void 0) { intervalNum = 1; }
    if (scalingToManipulatives === void 0) { scalingToManipulatives = false; }
    var pxPerUnit = computePxPerUnit(gridSquaresPerUnit, intervalNum, scalingToManipulatives);
    return (endNum - startNum) * pxPerUnit;
};
/**
 * Get gridSquaresPerUnit from scale mode.
 */
export var getGridSquaresPerUnit = function (scaleMode) {
    var _a, _b;
    return (_b = (_a = SCALE_MODES[scaleMode]) === null || _a === void 0 ? void 0 : _a.gridSquaresPerUnit) !== null && _b !== void 0 ? _b : 1;
};
/**
 * Snap a pixel position to the nearest tick mark.
 */
export var snapToTickMark = function (px, ticks) {
    if (ticks.length === 0)
        return px;
    var closest = ticks[0].px;
    var minDist = Math.abs(px - closest);
    for (var i = 1; i < ticks.length; i++) {
        var dist = Math.abs(px - ticks[i].px);
        if (dist < minDist) {
            minDist = dist;
            closest = ticks[i].px;
        }
    }
    return closest;
};
/**
 * Snap a position to the nearest grid boundary.
 */
export var snapToGrid = function (px, gridSize) {
    if (gridSize === void 0) { gridSize = GRID_SIZE; }
    return Math.round(px / gridSize) * gridSize;
};
/**
 * Get the absolute canvas geometry of a number line.
 */
export var findParentLineGeometry = function (parentLine) {
    var scale = parentLine.scale || 1;
    var pxPerUnit = computePxPerUnit(parentLine.gridSquaresPerUnit, parentLine.intervalNum, !!parentLine.scalingToManipulatives);
    // Return world-space values (accounting for NL scale)
    var lineWidth = (parentLine.endNum - parentLine.startNum) * pxPerUnit;
    return {
        lineWorldY: parentLine.center.y,
        lineStartX: parentLine.center.x - (lineWidth / 2) * scale,
        pxPerUnit: pxPerUnit * scale,
    };
};
/**
 * Collect left and right edge X positions of sibling jump bars.
 */
export var collectJumpBarEdges = function (jumpBars, excludeId) {
    var edges = [];
    jumpBars.forEach(function (jb) {
        if (jb.id === excludeId)
            return;
        edges.push(jb.center.x);
        edges.push(jb.center.x + jb.width);
    });
    return edges;
};
/**
 * Snap a value to the nearest jump bar edge if within threshold.
 */
export var snapToJumpBarEdge = function (value, edges, threshold) {
    var closest = value;
    var minDist = threshold + 1;
    for (var _i = 0, edges_1 = edges; _i < edges_1.length; _i++) {
        var edge = edges_1[_i];
        var dist = Math.abs(value - edge);
        if (dist < minDist) {
            minDist = dist;
            closest = edge;
        }
    }
    return minDist <= threshold ? closest : value;
};
/**
 * Snap a tile (Fraction Tile or Colored Tile) to the nearest Number Line
 * that has `scalingToManipulatives` enabled.
 *
 * Returns snapped center position, scale, and the NL id, or null if no NL is nearby.
 */
/**
 * Snap a tile to a Number Line.
 *
 * @param tileTopLeft  Top-left corner of the tile (how FractionTile/ColoredTile `center` works)
 * @param tileDimensions  Unscaled width/height
 * @param tileScale  Current scale of the tile
 * @param isCentered  If true, tileTopLeft is actually the geometric center (ColoredTiles use center)
 */
export var snapTileToNumberLine = function (tileTopLeft, tileDimensions, tileScale, manipulatives, tileId, threshold, isCentered) {
    if (threshold === void 0) { threshold = NL_TILE_SNAP_THRESHOLD; }
    if (isCentered === void 0) { isCentered = false; }
    // Find all number lines with scaling enabled
    var numberLines = manipulatives.filter(function (m) { return m.data.type === 'numberLine' && m.data.scalingToManipulatives; });
    if (numberLines.length === 0)
        return null;
    // Compute tile's geometric center from its anchor point
    var scaledW = tileDimensions.width * tileScale;
    var scaledH = tileDimensions.height * tileScale;
    var tileCenterY = isCentered ? tileTopLeft.y : tileTopLeft.y + scaledH / 2;
    var bestNL = null;
    var bestDist = Infinity;
    for (var _i = 0, numberLines_1 = numberLines; _i < numberLines_1.length; _i++) {
        var m = numberLines_1[_i];
        var nl = m.data;
        var geo_1 = findParentLineGeometry(nl);
        var dist = Math.abs(tileCenterY - geo_1.lineWorldY);
        if (dist < threshold + scaledH / 2 && dist < bestDist) {
            bestDist = dist;
            bestNL = nl;
        }
    }
    if (!bestNL)
        return null;
    var nlScale = bestNL.scale || 1;
    var geo = findParentLineGeometry(bestNL);
    var newScaledH = tileDimensions.height * nlScale;
    // Vertical snap: tile edge flush with the NL line
    var isAbove = tileCenterY < geo.lineWorldY;
    var snappedY;
    if (isAbove) {
        // Tile bottom edge aligns with the NL line
        snappedY = isCentered ? geo.lineWorldY - newScaledH / 2 : geo.lineWorldY - newScaledH;
    }
    else {
        // Tile top edge aligns with the NL line
        snappedY = isCentered ? geo.lineWorldY + newScaledH / 2 : geo.lineWorldY;
    }
    // Horizontal snap: align tile's left edge to nearest tick mark
    var pxPerUnitUnscaled = computePxPerUnit(bestNL.gridSquaresPerUnit, bestNL.intervalNum, !!bestNL.scalingToManipulatives);
    var ticks = computeTickPositions(bestNL.startNum, bestNL.endNum, bestNL.intervalNum, pxPerUnitUnscaled);
    // Left edge in world space
    var leftEdge = isCentered ? tileTopLeft.x - scaledW / 2 : tileTopLeft.x;
    // Convert to line-local unscaled px
    var leftEdgeLocal = (leftEdge - geo.lineStartX) / nlScale;
    // Snap left edge to nearest tick. The tick itself is a 0-width box with a
    // TICK_BORDER_WIDTH border rendered on each side (see numberLineManipulative
    // .scss `.numberLineTick`), centered on its coordinate via
    // `transform: translate(-50%, -50%)` — so the tick's own rendered stroke
    // occupies [tickX - TICK_BORDER_WIDTH, tickX + TICK_BORDER_WIDTH]. Subtract
    // half the tick's border width so the tile's edge lines up with the tick
    // mark's own edge rather than sitting the tick's full width past it.
    var snappedLocalPx = snapToTickMark(leftEdgeLocal, ticks);
    var snappedLeftEdge = geo.lineStartX + snappedLocalPx * nlScale - TICK_BORDER_WIDTH;
    var snappedX = isCentered ? snappedLeftEdge + (tileDimensions.width * nlScale) / 2 : snappedLeftEdge;
    return {
        x: snappedX,
        y: snappedY,
        scale: nlScale,
        numberLineId: bestNL.id,
    };
};
